import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import styles from './PriceList.module.css';
import Loader from '../Loader';
import BulkUploadModal from '../component/BulkUploadModal';

const API = process.env.REACT_APP_API_BASE_URL;
const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const USER_TYPES = ['Thru Sales Guys', 'Direct', 'All Medical Colleges', 'Belle Vue Hospital Kolkata', 'Mercury Hospital Chennai', 'RF20', 'RF10', 'Brains Hospital Bangalore', 'ZYDUS Hospital', 'All Hospitals','GANGARAM','Medical College','MERCURY A','MERCURY B'];

/* ─── Format currency ─── */
const formatPrice = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

/* ─── Modal wrapper ─── */
const Modal = ({ title, onClose, children }) => (
    <div className={styles.modalBackdrop} onClick={onClose}>
        <div className={styles.modalContainer} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
                <h3>{title}</h3>
                <button className={styles.closeBtn} onClick={onClose}>&times;</button>
            </div>
            <div className={styles.modalBody}>{children}</div>
        </div>
    </div>
);

const PriceList = () => {
    const [priceList, setPriceList] = useState([]);
    const [aimsCharges, setAimsCharges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('pricelist');

    // Filters & sorting
    const [searchQuery, setSearchQuery] = useState('');
    const [userTypeFilter, setUserTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showAll, setShowAll] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'test_name', direction: 'ascending' });

    // AIMS charges filter
    const [aimsSearch, setAimsSearch] = useState('');

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Test list for dropdown
    const [testList, setTestList] = useState([]);

    // Form fields
    const [formUserType, setFormUserType] = useState('Thru Sales Guys');
    const [formTestName, setFormTestName] = useState('');
    const [formSerum, setFormSerum] = useState('');
    const [formCsf, setFormCsf] = useState('');
    const [formSerumCsf, setFormSerumCsf] = useState('');

    // AIMS form fields
    const [aimsFormTestName, setAimsFormTestName] = useState('');
    const [aimsFormCharge, setAimsFormCharge] = useState('');

    // Edit/delete targets
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [editAimsTarget, setEditAimsTarget] = useState(null);
    const [deleteAimsTarget, setDeleteAimsTarget] = useState(null);

    // AIMS modal states
    const [showAimsAddModal, setShowAimsAddModal] = useState(false);
    const [showAimsEditModal, setShowAimsEditModal] = useState(false);
    const [showAimsDeleteConfirm, setShowAimsDeleteConfirm] = useState(false);

    // Bulk upload states
    const [showAimsBulkUpload, setShowAimsBulkUpload] = useState(false);
    const [showPriceListBulkUpload, setShowPriceListBulkUpload] = useState(false);

    /* ─── Fetch test list for dropdown ─── */
    const fetchTestList = async () => {
        try {
            const { data } = await axios.get(`${API}/api/testlist`, authHeaders());
            setTestList(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to load test list', err);
        }
    };

    useEffect(() => { fetchTestList(); }, []);

    /* ─── Fetch data ─── */
    const fetchPriceList = async () => {
        try {
            const endpoint = showAll ? '/api/testlist/pricelist/all' : '/api/testlist/pricelist';
            const config = showAll ? authHeaders() : {};
            const response = await axios.get(`${API}${endpoint}`, config);
            setPriceList(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching price list:', err);
            toast.error('Failed to fetch price list');
        }
    };

    const fetchAimsCharges = async () => {
        try {
            const response = await axios.get(`${API}/api/testlist/aimscharges`, authHeaders());
            setAimsCharges(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching AIMS charges:', err);
        }
    };

    const fetchAll = async () => {
        setLoading(true);
        await Promise.all([fetchPriceList(), fetchAimsCharges()]);
        setLoading(false);
    };

    useEffect(() => { fetchAll(); }, []); // eslint-disable-line

    useEffect(() => {
        fetchPriceList();
    }, [showAll]); // eslint-disable-line

    /* ─── Filtered & sorted data ─── */
    const filteredPriceList = useMemo(() => {
        let items = priceList;

        if (statusFilter === 'active') {
            items = items.filter(i => i.isActive !== false);
        } else if (statusFilter === 'inactive') {
            items = items.filter(i => i.isActive === false);
        }

        if (userTypeFilter !== 'all') {
            items = items.filter(i => i.user_type === userTypeFilter);
        }

        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            items = items.filter(i => i.test_name.toLowerCase().includes(q));
        }

        if (sortConfig.key) {
            items = [...items].sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                if (aVal === null || aVal === undefined) aVal = '';
                if (bVal === null || bVal === undefined) bVal = '';
                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }

        return items;
    }, [priceList, searchQuery, userTypeFilter, statusFilter, sortConfig]);

    const filteredAimsCharges = useMemo(() => {
        if (!aimsSearch.trim()) return aimsCharges;
        const q = aimsSearch.toLowerCase();
        return aimsCharges.filter(i => i.test.toLowerCase().includes(q));
    }, [aimsCharges, aimsSearch]);

    /* ─── Summary counts ─── */
    const totalCount = priceList.length;
    const activeCount = priceList.filter(i => i.isActive !== false).length;
    const inactiveCount = priceList.filter(i => i.isActive === false).length;

    /* ─── Sorting ─── */
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending'
        }));
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return '';
        return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
    };

    /* ─── Reset form ─── */
    const resetForm = () => {
        setFormUserType('Thru Sales Guys');
        setFormTestName('');
        setFormSerum('');
        setFormCsf('');
        setFormSerumCsf('');
    };

    /* ─── Add ─── */
    const handleAdd = async () => {
        if (!formUserType || !formTestName.trim()) {
            toast.error('User type and test name are required');
            return;
        }
        try {
            setSaving(true);
            await axios.post(`${API}/api/testlist/pricelist`, {
                user_type: formUserType,
                test_name: formTestName.trim(),
                serum: formSerum ? Number(formSerum) : null,
                csf: formCsf ? Number(formCsf) : null,
                serum_csf: formSerumCsf ? Number(formSerumCsf) : null
            }, authHeaders());
            toast.success('Price entry created successfully');
            setShowAddModal(false);
            resetForm();
            await fetchPriceList();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to create price entry';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const resetAimsForm = () => {
        setAimsFormTestName('');
        setAimsFormCharge('');
    };

    const exportPriceListToExcel = () => {
        if (filteredPriceList.length === 0) {
            toast.warn('No data to export');
            return;
        }
        const rows = filteredPriceList.map((item, index) => ({
            'Sl. No.': index + 1,
            'Test Name': item.test_name || '',
            'User Type': item.user_type || '',
            'Serum (₹)': item.serum ?? '',
            'CSF (₹)': item.csf ?? '',
            'Serum + CSF (₹)': item.serum_csf ?? '',
            'Status': item.isActive !== false ? 'Active' : 'Inactive',
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const colWidths = Object.keys(rows[0]).map(key => ({
            wch: Math.min(Math.max(key.length, ...rows.map(r => String(r[key] ?? '').length)) + 2, 36)
        }));
        worksheet['!cols'] = colWidths;
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Price List');
        XLSX.writeFile(workbook, 'Price_List.xlsx');
    };

    const exportAimsChargesToExcel = () => {
        if (filteredAimsCharges.length === 0) {
            toast.warn('No data to export');
            return;
        }
        const rows = filteredAimsCharges.map((item, index) => ({
            'Sl. No.': item.Sl_no ?? index + 1,
            'Test': item.test,
            'AIMS Charge (₹)': item.aimsCharge ?? 0,
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const colWidths = Object.keys(rows[0]).map(key => ({
            wch: Math.min(Math.max(key.length, ...rows.map(r => String(r[key] ?? '').length)) + 2, 40)
        }));
        worksheet['!cols'] = colWidths;
        Object.keys(rows[0]).forEach((_, colIndex) => {
            const cell = XLSX.utils.encode_cell({ r: 0, c: colIndex });
            if (worksheet[cell]) {
                worksheet[cell].s = {
                    fill: { fgColor: { rgb: 'FFFF00' } },
                    font: { bold: true, color: { rgb: '000000' } },
                    alignment: { horizontal: 'center' },
                };
            }
        });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'AIMS Charges');
        XLSX.writeFile(workbook, 'AIMS_Charges.xlsx');
    };

    const getAimsChargeId = (item) => item?.id ?? item?._id ?? item?.Sl_no ?? item?.sl_no;

    /* ─── Bulk Import: AIMS Charges ─── */
    const handleAimsBulkImport = async (rows) => {
        let successCount = 0;
        const failed = [];
        for (const row of rows) {
            try {
                await axios.post(`${API}/api/testlist/aimscharges`, {
                    test: String(row['Test']).trim(),
                    aimsCharge: Number(row['AIMS Charge']),
                }, authHeaders());
                successCount++;
            } catch (err) {
                failed.push(String(row['Test']).trim());
            }
        }
        if (successCount > 0) toast.success(`${successCount} AIMS charge(s) imported successfully`);
        if (failed.length > 0) toast.error(`${failed.length} record(s) failed to import: ${failed.join(', ')}`);
        await fetchAimsCharges();
        setShowAimsBulkUpload(false);
    };

    /* ─── Bulk Import: Price List ─── */
    const handlePriceListBulkImport = async (rows) => {
        let successCount = 0;
        const failed = [];
        for (const row of rows) {
            try {
                await axios.post(`${API}/api/testlist/pricelist`, {
                    user_type: String(row['User Type']).trim(),
                    test_name: String(row['Test Name']).trim(),
                    serum: row['Serum'] !== '' && row['Serum'] !== undefined ? Number(row['Serum']) : null,
                    csf: row['CSF'] !== '' && row['CSF'] !== undefined ? Number(row['CSF']) : null,
                    serum_csf: row['Serum + CSF'] !== '' && row['Serum + CSF'] !== undefined ? Number(row['Serum + CSF']) : null,
                }, authHeaders());
                successCount++;
            } catch (err) {
                failed.push(String(row['Test Name']).trim());
            }
        }
        if (successCount > 0) toast.success(`${successCount} price list entry(ies) imported successfully`);
        if (failed.length > 0) toast.error(`${failed.length} record(s) failed to import: ${failed.join(', ')}`);
        await fetchPriceList();
        setShowPriceListBulkUpload(false);
    };

    const handleAddAims = async () => {
        if (!aimsFormTestName.trim()) {
            toast.error('Test name is required for AIMS charge');
            return;
        }
        if (aimsFormCharge === '' || isNaN(Number(aimsFormCharge))) {
            toast.error('Enter a valid AIMS charge');
            return;
        }
        try {
            setSaving(true);
            await axios.post(`${API}/api/testlist/aimscharges`, {
                test: aimsFormTestName.trim(),
                aimsCharge: Number(aimsFormCharge)
            }, authHeaders());
            toast.success('AIMS charge added successfully');
            setShowAimsAddModal(false);
            resetAimsForm();
            await fetchAimsCharges();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to add AIMS charge';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const openAimsEdit = (item) => {
        setEditAimsTarget(item);
        setAimsFormTestName(item.test || '');
        setAimsFormCharge(item.aimsCharge ?? '');
        setShowAimsEditModal(true);
    };

    const handleEditAims = async () => {
        if (!editAimsTarget) return;
        if (!aimsFormTestName.trim()) {
            toast.error('Test name is required for AIMS charge');
            return;
        }
        if (aimsFormCharge === '' || isNaN(Number(aimsFormCharge))) {
            toast.error('Enter a valid AIMS charge');
            return;
        }
        const targetId = getAimsChargeId(editAimsTarget);
        if (!targetId) {
            toast.error('Unable to determine AIMS charge ID for update');
            return;
        }
        try {
            setSaving(true);
            await axios.put(`${API}/api/testlist/aimscharges/${targetId}`, {
                test: aimsFormTestName.trim(),
                aimsCharge: Number(aimsFormCharge)
            }, authHeaders());
            toast.success('AIMS charge updated successfully');
            setShowAimsEditModal(false);
            setEditAimsTarget(null);
            resetAimsForm();
            await fetchAimsCharges();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to update AIMS charge';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAims = async () => {
        if (!deleteAimsTarget) return;
        const targetId = getAimsChargeId(deleteAimsTarget);
        if (!targetId) {
            toast.error('Unable to determine AIMS charge ID for delete');
            return;
        }
        try {
            setSaving(true);
            await axios.delete(`${API}/api/testlist/aimscharges/${targetId}`, authHeaders());
            toast.success('AIMS charge deleted successfully');
            setDeleteAimsTarget(null);
            setShowAimsDeleteConfirm(false);
            await fetchAimsCharges();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to delete AIMS charge';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    /* ─── Open Edit ─── */
    const openEdit = (item) => {
        setEditTarget(item);
        setFormUserType(item.user_type);
        setFormTestName(item.test_name);
        setFormSerum(item.serum ?? '');
        setFormCsf(item.csf ?? '');
        setFormSerumCsf(item.serum_csf ?? '');
        setShowEditModal(true);
    };

    /* ─── Save Edit ─── */
    const handleEdit = async () => {
        if (!formUserType || !formTestName.trim()) {
            toast.error('User type and test name are required');
            return;
        }
        try {
            setSaving(true);
            await axios.put(`${API}/api/testlist/pricelist/${editTarget.id}`, {
                user_type: formUserType,
                test_name: formTestName.trim(),
                serum: formSerum !== '' ? Number(formSerum) : null,
                csf: formCsf !== '' ? Number(formCsf) : null,
                serum_csf: formSerumCsf !== '' ? Number(formSerumCsf) : null
            }, authHeaders());
            toast.success('Price entry updated successfully');
            setShowEditModal(false);
            setEditTarget(null);
            resetForm();
            await fetchPriceList();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to update price entry';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    /* ─── Delete (soft) ─── */
    const handleDelete = async () => {
        try {
            setSaving(true);
            await axios.delete(`${API}/api/testlist/pricelist/${deleteTarget.id}`, authHeaders());
            toast.success('Price entry deleted successfully');
            setShowDeleteConfirm(false);
            setDeleteTarget(null);
            await fetchPriceList();
        } catch (err) {
            toast.error('Failed to delete price entry');
        } finally {
            setSaving(false);
        }
    };

    /* ─── Restore ─── */
    const handleRestore = async (item) => {
        try {
            await axios.patch(`${API}/api/testlist/pricelist/${item.id}/restore`, {}, authHeaders());
            toast.success('Price entry restored successfully');
            await fetchPriceList();
        } catch (err) {
            toast.error('Failed to restore price entry');
        }
    };

    /* ─── Toggle status via badge click ─── */
    const handleToggleStatus = async (item) => {
        if (item.isActive === false) {
            await handleRestore(item);
        } else {
            setDeleteTarget(item);
            setShowDeleteConfirm(true);
        }
    };

    if (loading) return <Loader />;

    return (
        <div className={styles.container}>
            {/* ─── Sub-Tabs ─── */}
            <div className={styles.tabRow}>
                <button
                    className={`${styles.tabBtn} ${activeTab === 'pricelist' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('pricelist')}
                >
                    Price List
                </button>
                <button
                    className={`${styles.tabBtn} ${activeTab === 'aimscharges' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('aimscharges')}
                >
                    AIMS Charges
                </button>
            </div>

            {/* ═══════════ PRICE LIST TAB ═══════════ */}
            {activeTab === 'pricelist' && (
                <>
                    {/* Header Row */}
                    <div className={styles.headerRow}>
                        <h2 className={styles.title}>Price List</h2>
                    </div>

                    {/* Summary Cards */}
                    <div className={styles.summaryCards}>
                        <div className={styles.summaryCard}>
                            <span className={styles.summaryLabel}>Total</span>
                            <span className={styles.summaryValue}>{totalCount}</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.activeCard}`}>
                            <span className={styles.summaryLabel}>Active</span>
                            <span className={styles.summaryValue}>{activeCount}</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.inactiveCard}`}>
                            <span className={styles.summaryLabel}>Inactive</span>
                            <span className={styles.summaryValue}>{inactiveCount}</span>
                        </div>
                    </div>

                    {/* Filters Row */}
                    <div className={styles.filtersRow}>
                        <div className={styles.searchWrapper}>
                            <span className={styles.searchIcon}>🔍</span>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search by test name..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className={styles.filterGroup}>
                            <select
                                className={styles.filterSelect}
                                value={userTypeFilter}
                                onChange={e => setUserTypeFilter(e.target.value)}
                            >
                                <option value="all">All User Types</option>
                                {USER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>

                        <div className={styles.statusFilterGroup}>
                            <button
                                className={`${styles.filterBtn} ${statusFilter === 'all' ? styles.filterBtnActive : ''}`}
                                onClick={() => setStatusFilter('all')}
                            >All</button>
                            <button
                                className={`${styles.filterBtn} ${styles.filterBtnGreen} ${statusFilter === 'active' ? styles.filterBtnActive : ''}`}
                                onClick={() => setStatusFilter('active')}
                            >Active</button>
                            <button
                                className={`${styles.filterBtn} ${styles.filterBtnRed} ${statusFilter === 'inactive' ? styles.filterBtnActive : ''}`}
                                onClick={() => setStatusFilter('inactive')}
                            >Inactive</button>
                        </div>

                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginLeft: 'auto' }}>
                            <label className={styles.toggleLabel}>
                                <input
                                    type="checkbox"
                                    checked={showAll}
                                    onChange={(e) => setShowAll(e.target.checked)}
                                />
                                <span>Show Inactive</span>
                            </label>
                            <button className={styles.exportBtn} onClick={exportPriceListToExcel}>
                                Export Excel
                            </button>
                            <button className={styles.bulkUploadBtn} onClick={() => setShowPriceListBulkUpload(true)}>
                                Bulk Upload
                            </button>
                            <button className={styles.addBtn} onClick={() => { resetForm(); setShowAddModal(true); }}>
                                + Add Price Entry
                            </button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.sortable} onClick={() => handleSort('test_name')}>
                                        Test Name{getSortIndicator('test_name')}
                                    </th>
                                    <th className={styles.sortable} onClick={() => handleSort('user_type')}>
                                        User Type{getSortIndicator('user_type')}
                                    </th>
                                    <th>Serum</th>
                                    <th>CSF</th>
                                    <th>Serum + CSF</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPriceList.length === 0 ? (
                                    <tr><td colSpan="7" className={styles.noData}>No price entries found</td></tr>
                                ) : (
                                    filteredPriceList.map(item => (
                                        <tr key={item.id} className={item.isActive === false ? styles.inactiveRow : ''}>
                                            <td>{item.test_name}</td>
                                            <td>
                                                <span className={`${styles.userTypeBadge} ${styles[`type${item.user_type}`]}`}>
                                                    {item.user_type}
                                                </span>
                                            </td>
                                            <td>{formatPrice(item.serum)}</td>
                                            <td>{formatPrice(item.csf)}</td>
                                            <td>{formatPrice(item.serum_csf)}</td>
                                            <td>
                                                <span
                                                    className={`${styles.statusBadge} ${item.isActive !== false ? styles.activeBadge : styles.inactiveBadge}`}
                                                    onClick={() => handleToggleStatus(item)}
                                                    title={item.isActive !== false ? 'Click to deactivate' : 'Click to restore'}
                                                >
                                                    {item.isActive !== false ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className={styles.actionCell}>
                                                <button className={styles.editBtn} onClick={() => openEdit(item)} title="Edit">✏️</button>
                                                {item.isActive !== false ? (
                                                    <button
                                                        className={styles.deleteIconBtn}
                                                        onClick={() => { setDeleteTarget(item); setShowDeleteConfirm(true); }}
                                                        title="Delete"
                                                    >🗑️</button>
                                                ) : (
                                                    <button
                                                        className={styles.restoreBtn}
                                                        onClick={() => handleRestore(item)}
                                                        title="Restore"
                                                    >♻️</button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* ═══════════ AIMS CHARGES TAB ═══════════ */}
            {activeTab === 'aimscharges' && (
                <>
                    <div className={styles.headerRow}>
                        <h2 className={styles.title}>AIMS Charges</h2>
                    </div>

                    {/* ─── Toolbar: search (left) · actions (right) ─── */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '1 1 auto' }}>
                            <div className={styles.searchWrapper}>
                                <span className={styles.searchIcon}>🔍</span>
                                <input
                                    type="text"
                                    className={styles.searchInput}
                                    placeholder="Search by test name..."
                                    value={aimsSearch}
                                    onChange={e => setAimsSearch(e.target.value)}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <button className={styles.exportBtn} onClick={exportAimsChargesToExcel}>
                                Export Excel
                            </button>
                            <button className={styles.bulkUploadBtn} onClick={() => setShowAimsBulkUpload(true)}>
                                Bulk Upload
                            </button>
                            <button className={styles.addBtn} onClick={() => { resetAimsForm(); setShowAimsAddModal(true); }}>
                                + Add AIMS Charge
                            </button>
                        </div>
                    </div>
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Sl. No.</th>
                                    <th>Test</th>
                                    <th>AIMS Charge</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAimsCharges.length === 0 ? (
                                    <tr><td colSpan="4" className={styles.noData}>No AIMS charges found</td></tr>
                                ) : (
                                    filteredAimsCharges.map((item, index) => (
                                        <tr key={item.id || item.Sl_no || index}>
                                            <td>{item.Sl_no ?? index + 1}</td>
                                            <td>{item.test}</td>
                                            <td>{formatPrice(item.aimsCharge)}</td>
                                            <td className={styles.actionCell}>
                                                <button className={styles.editBtn} onClick={() => openAimsEdit(item)} title="Edit">
                                                    ✏️
                                                </button>
                                                <button
                                                    className={styles.deleteIconBtn}
                                                    onClick={() => { setDeleteAimsTarget(item); setShowAimsDeleteConfirm(true); }}
                                                    title="Delete"
                                                >
                                                    🗑️
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* ═══════════ ADD MODAL ═══════════ */}
            {showAddModal && (
                <Modal title="Add Price Entry" onClose={() => { setShowAddModal(false); resetForm(); }}>
                    <div className={styles.formGroup}>
                        <label>User Type <span className={styles.required}>*</span></label>
                        <select value={formUserType} onChange={e => setFormUserType(e.target.value)} className={styles.formSelect}>
                            {USER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Test Name <span className={styles.required}>*</span></label>
                        <select value={formTestName} onChange={e => setFormTestName(e.target.value)} className={styles.formSelect}>
                            <option value="">Select Test</option>
                            {testList.map(t => <option key={t.id || t.TestName} value={t.TestName}>{t.TestName}</option>)}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Serum Price</label>
                        <input type="number" value={formSerum} onChange={e => setFormSerum(e.target.value)} placeholder="e.g. 1500" min="0" step="0.01" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>CSF Price</label>
                        <input type="number" value={formCsf} onChange={e => setFormCsf(e.target.value)} placeholder="e.g. 1800" min="0" step="0.01" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Serum + CSF Price</label>
                        <input type="number" value={formSerumCsf} onChange={e => setFormSerumCsf(e.target.value)} placeholder="e.g. 2500" min="0" step="0.01" />
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.cancelBtn} onClick={() => { setShowAddModal(false); resetForm(); }}>Cancel</button>
                        <button className={styles.saveBtn} onClick={handleAdd} disabled={saving}>
                            {saving ? 'Saving...' : 'Add Entry'}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ═══════════ EDIT MODAL ═══════════ */}
            {showEditModal && (
                <Modal title="Edit Price Entry" onClose={() => { setShowEditModal(false); setEditTarget(null); resetForm(); }}>
                    <div className={styles.formGroup}>
                        <label>User Type <span className={styles.required}>*</span></label>
                        <select value={formUserType} onChange={e => setFormUserType(e.target.value)} className={styles.formSelect}>
                            {USER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Test Name <span className={styles.required}>*</span></label>
                        <select value={formTestName} onChange={e => setFormTestName(e.target.value)} className={styles.formSelect}>
                            <option value="">Select Test</option>
                            {testList.map(t => <option key={t.id || t.TestName} value={t.TestName}>{t.TestName}</option>)}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Serum Price</label>
                        <input type="number" value={formSerum} onChange={e => setFormSerum(e.target.value)} placeholder="e.g. 1500" min="0" step="0.01" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>CSF Price</label>
                        <input type="number" value={formCsf} onChange={e => setFormCsf(e.target.value)} placeholder="e.g. 1800" min="0" step="0.01" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Serum + CSF Price</label>
                        <input type="number" value={formSerumCsf} onChange={e => setFormSerumCsf(e.target.value)} placeholder="e.g. 2500" min="0" step="0.01" />
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.cancelBtn} onClick={() => { setShowEditModal(false); setEditTarget(null); resetForm(); }}>Cancel</button>
                        <button className={styles.saveBtn} onClick={handleEdit} disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ═══════════ AIMS ADD MODAL ═══════════ */}
            {showAimsAddModal && (
                <Modal title="Add AIMS Charge" onClose={() => { setShowAimsAddModal(false); resetAimsForm(); }}>
                    <div className={styles.formGroup}>
                        <label>Test Name <span className={styles.required}>*</span></label>
                        <select value={aimsFormTestName} onChange={e => setAimsFormTestName(e.target.value)} className={styles.formSelect}>
                            <option value="">Select Test</option>
                            {testList.map(t => <option key={t.id || t.TestName} value={t.TestName}>{t.TestName}</option>)}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label>AIMS Charge <span className={styles.required}>*</span></label>
                        <input type="number" value={aimsFormCharge} onChange={e => setAimsFormCharge(e.target.value)} placeholder="e.g. 1200" min="0" step="0.01" />
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.cancelBtn} onClick={() => { setShowAimsAddModal(false); resetAimsForm(); }}>Cancel</button>
                        <button className={styles.saveBtn} onClick={handleAddAims} disabled={saving}>
                            {saving ? 'Saving...' : 'Add AIMS Charge'}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ═══════════ AIMS EDIT MODAL ═══════════ */}
            {showAimsEditModal && (
                <Modal title="Edit AIMS Charge" onClose={() => { setShowAimsEditModal(false); setEditAimsTarget(null); resetAimsForm(); }}>
                    <div className={styles.formGroup}>
                        <label>Test Name <span className={styles.required}>*</span></label>
                        <select value={aimsFormTestName} onChange={e => setAimsFormTestName(e.target.value)} className={styles.formSelect}>
                            <option value="">Select Test</option>
                            {testList.map(t => <option key={t.id || t.TestName} value={t.TestName}>{t.TestName}</option>)}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label>AIMS Charge <span className={styles.required}>*</span></label>
                        <input type="number" value={aimsFormCharge} onChange={e => setAimsFormCharge(e.target.value)} placeholder="e.g. 1200" min="0" step="0.01" />
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.cancelBtn} onClick={() => { setShowAimsEditModal(false); setEditAimsTarget(null); resetAimsForm(); }}>Cancel</button>
                        <button className={styles.saveBtn} onClick={handleEditAims} disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ═══════════ AIMS DELETE CONFIRM ═══════════ */}
            {showAimsDeleteConfirm && deleteAimsTarget && (
                <Modal title="Confirm Delete" onClose={() => { setShowAimsDeleteConfirm(false); setDeleteAimsTarget(null); }}>
                    <p>Are you sure you want to delete this AIMS charge?</p>
                    <div className={styles.deleteDetails}>
                        <span><strong>Test:</strong> {deleteAimsTarget.test}</span>
                        <span><strong>AIMS Charge:</strong> {formatPrice(deleteAimsTarget.aimsCharge)}</span>
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.cancelBtn} onClick={() => { setShowAimsDeleteConfirm(false); setDeleteAimsTarget(null); }}>Cancel</button>
                        <button className={styles.deleteBtn} onClick={handleDeleteAims} disabled={saving}>
                            {saving ? 'Deleting...' : 'Yes, Delete'}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ═══════════ DELETE CONFIRM ═══════════ */}
            {showDeleteConfirm && deleteTarget && (
                <Modal title="Confirm Delete" onClose={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}>
                    <p>Are you sure you want to deactivate this price entry?</p>
                    <div className={styles.deleteDetails}>
                        <span><strong>Test:</strong> {deleteTarget.test_name}</span>
                        <span><strong>User Type:</strong> {deleteTarget.user_type}</span>
                        {deleteTarget.serum && <span><strong>Serum:</strong> {formatPrice(deleteTarget.serum)}</span>}
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.cancelBtn} onClick={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}>Cancel</button>
                        <button className={styles.deleteBtn} onClick={handleDelete} disabled={saving}>
                            {saving ? 'Deleting...' : 'Yes, Deactivate'}
                        </button>
                    </div>
                </Modal>
            )}

            {/* ═══════════ BULK UPLOAD MODALS ═══════════ */}
            {showAimsBulkUpload && (
                <BulkUploadModal
                    type="aims"
                    testList={testList}
                    userTypes={USER_TYPES}
                    onImport={handleAimsBulkImport}
                    onClose={() => setShowAimsBulkUpload(false)}
                />
            )}
            {showPriceListBulkUpload && (
                <BulkUploadModal
                    type="pricelist"
                    testList={testList}
                    userTypes={USER_TYPES}
                    onImport={handlePriceListBulkImport}
                    onClose={() => setShowPriceListBulkUpload(false)}
                />
            )}
        </div>
    );
};

export default PriceList;
